import React, { useEffect, useState } from "react";
import {
  Shield,
  CheckCircle,
  FileCheck,
  Briefcase,
  Lock,
  Database,
  Search,
} from "lucide-react";
import HeaderWithSidePanel from '../HomeScreen/handlebar';


const EmploymentVerification = () => {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-fadeIn");
            entry.target.classList.remove("opacity-0");
          }
        });
      },
      { threshold: 0.1 }
    );

    const hiddenElements = document.querySelectorAll(".initially-hidden");
    hiddenElements.forEach((el) => observer.observe(el));

    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 3);
    }, 3000);

    return () => {
      hiddenElements.forEach((el) => observer.unobserve(el));
      clearInterval(interval);
    };
  }, []);

  const processSteps = [
    {
      title: "HR Contact",
      description:
        "The system sends an email with a set of questions to the appropriate HR personnel",
      icon: <FileCheck className="w-8 h-8 text-red-300" />,
      image:
        "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80",
    },
    {
      title: "Behavioral Questions",
      description:
        "Detailed questions are sent focusing on specific aspects of the candidate's professional behavior",
      icon: <Search className="w-8 h-8 text-red-300" />,
      image:
        "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80",
    },
    {
      title: "Email Record",
      description:
        "The email exchange provides documented evidence of the verification process",
      icon: <Database className="w-8 h-8 text-red-300" />,
      image:
        "https://images.pexels.com/photos/265087/pexels-photo-265087.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-950 to-gray-900 text-white pt-0 lg:pt-0">      
    {/* <HeaderWithSidePanel/> */}
      {/* Hero Section */}
      <section className="relative h-[60vh] overflow-hidden">
        
        <div className="absolute inset-0 bg-gradient-to-b from-red-900/60 to-red-900/90 z-10"></div>

        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1950&q=80"
            alt="Employment Background"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="relative z-20 container mx-auto px-4 h-full flex flex-col justify-center">
          <div className="max-w-3xl">
            <div className="inline-block mb-4">
              <div className="bg-white/10 p-3 rounded-full animate-pulse">
                <Shield className="h-8 w-8 text-red-300" />
              </div>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-4 animate-fadeInDown">
              Employment Verification
            </h1>
            <p className="text-xl text-red-200 mb-8 max-w-2xl animate-fadeInUp">
              Confirming candidate identity, work history, and skills to ensure
              a secure and qualified workforce.
            </p>
            <div className="flex flex-wrap gap-4 animate-fadeInUp">
              <button className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 flex items-center">
                <Lock className="w-5 h-5 mr-2" />
                Learn More
              </button>
              {/* <button className="bg-transparent border-2 border-red-400 hover:bg-red-400/20 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300">
                Watch Demo
              </button> */}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="container mx-auto px-4 py-16">
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-white/20 mb-16 initially-hidden opacity-0">
          <h2 className="text-3xl font-bold mb-8 text-center">
            Understanding Employment Verification
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div>
              <p className="text-lg leading-relaxed mb-6">
                Employers verify employment to confirm a candidate’s identity,
                work history, and skills, ensuring they meet job requirements
                and organizational standards.
              </p>
              <p className="text-lg leading-relaxed">
                This process often involves contacting previous employers,
                checking references, and validating qualifications through
                background checks and detailed inquiries.
              </p>

              <div className="mt-8 bg-red-900/30 p-6 rounded-lg border border-red-500/30">
                <h3 className="text-xl font-bold mb-4 flex items-center">
                  <Shield className="w-6 h-6 text-red-300 mr-2" />
                  Why It Matters
                </h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="text-red-300 mr-2">✓</span>
                    <span>Reduces employee turnover</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-300 mr-2">✓</span>
                    <span>Protects sensitive information</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-300 mr-2">✓</span>
                    <span>Verifies education and skills</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-300 mr-2">✓</span>
                    <span>Ensures a qualified workforce</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="relative rounded-xl overflow-hidden h-full shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80"
                alt="Employment Verification"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-red-900/80 to-transparent flex items-end">
                <p className="p-4 text-white font-medium">
                  Thorough verification ensures a reliable workforce
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Verification Process Animation */}
        <div className="mb-16 initially-hidden opacity-0">
          <h2 className="text-3xl font-bold mb-8 text-center">
            The Verification Process
          </h2>

          <div className="relative">
            <div className="hidden md:block absolute top-0 left-1/2 w-1 bg-red-500/50 h-full transform -translate-x-1/2 z-0"></div>

            {processSteps.map((step, index) => (
              <div
                key={index}
                className={`relative z-10 transition-all duration-500 mb-8 ${
                  activeStep === index ? "scale-100" : "scale-95 opacity-70"
                }`}
              >
                <div className="flex flex-col md:flex-row items-center">
                  <div
                    className={`w-full md:w-1/2 ${
                      index % 2 === 0
                        ? "md:pr-12 md:text-right"
                        : "md:order-2 md:pl-12"
                    }`}
                  >
                    <div
                      className={`bg-white/5 p-6 rounded-lg border border-white/10 transition-all duration-300 ${
                        activeStep === index
                          ? "bg-white/15 border-red-500/50 shadow-lg"
                          : ""
                      }`}
                    >
                      <div className="flex items-center mb-4 justify-center">
                        <div
                          className={`p-3 rounded-full ${
                            activeStep === index
                              ? "bg-red-600/30"
                              : "bg-white/10"
                          }`}
                        >
                          {step.icon}
                        </div>
                      </div>
                      <h3 className="text-xl font-bold text-red-300 mb-2">
                        {step.title}
                      </h3>
                      <p className="text-white/80">{step.description}</p>
                    </div>
                  </div>

                  <div className="absolute left-1/2 transform -translate-x-1/2 hidden md:flex items-center justify-center">
                    <div
                      className={`w-10 h-10 rounded-full border-4 z-20 flex items-center justify-center transition-all duration-300 ${
                        activeStep === index
                          ? "bg-red-600 border-red-900 scale-125"
                          : "bg-red-900/70 border-red-700/50"
                      }`}
                    >
                      <span className="text-white font-bold">{index + 1}</span>
                    </div>
                  </div>

                  <div
                    className={`w-full md:w-1/2 mt-4 md:mt-0 ${
                      index % 2 === 0 ? "md:order-2 md:pl-12" : "md:pr-12"
                    }`}
                  >
                    <div
                      className={`h-48 rounded-lg overflow-hidden transition-all duration-300 ${
                        activeStep === index
                          ? "shadow-2xl ring-2 ring-red-500/50"
                          : ""
                      }`}
                    >
                      <img
                        src={step.image}
                        alt={step.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Digital and Phycal Verification */}
        {/* Phycial Verification */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-white/20 mb-16 initially-hidden opacity-0">
          {/* <h2 className="text-3xl font-bold mb-8 text-center">
            Physical Verification
          </h2> */}

          <div className="grid gap-8">
            {/* <div className="bg-gradient-to-br from-red-800/30 to-red-600/30 p-6 rounded-xl border border-red-500/20">
              <h3 className="text-xl font-bold mb-4 flex items-center justify-center">
                <Database className="w-6 h-6 text-red-300 mr-2" />
                Digital Verification
              </h3>
              <p className="text-white/80 mb-4">
                Leveraging email communication and online systems for efficient,
                documented verification of employment history.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="text-red-300 mr-2">✓</span>
                  <span>Fast and scalable process</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-300 mr-2">✓</span>
                  <span>Secure digital records</span>
                </li>
              </ul>
            </div> */}

            <div className="bg-gradient-to-br from-red-800/30 to-red-600/30 p-6 rounded-xl border border-red-500/20">
              <h3 className="text-xl font-bold mb-4 flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-red-300 mr-2" />
                Physical Verification
              </h3>
              <p className="text-white/80 mb-4">
                In-person validation conducted when enhanced scrutiny is
                necessary.
              </p>
              <div className="bg-red-900/30 p-4 rounded-lg border border-red-500/30">
                <p className="text-red-200 italic">
                  Note: Physical verification may be conducted under specific
                  circumstances where additional validation is required.
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Best Practices */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-white/20 mb-16 initially-hidden opacity-0">
          <h2 className="text-3xl font-bold mb-8 text-center">
            Best Practices for Verification
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-red-800/30 to-red-600/30 p-6 rounded-xl border border-red-500/20">
              <h3 className="text-2xl font-bold mb-6 text-center">
                For Employers
              </h3>

              <div className="space-y-4">
                <div className="flex items-start p-3 bg-red-900/30 rounded-lg">
                  <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1 flex-shrink-0">
                    <CheckCircle className="h-4 w-4 text-red-300" />
                  </div>
                  <p>Verify employment history before final offers</p>
                </div>

                <div className="flex items-start p-3 bg-red-900/30 rounded-lg">
                  <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1 flex-shrink-0">
                    <CheckCircle className="h-4 w-4 text-red-300" />
                  </div>
                  <p>Use reputable background check providers</p>
                </div>

                <div className="flex items-start p-3 bg-red-900/30 rounded-lg">
                  <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1 flex-shrink-0">
                    <CheckCircle className="h-4 w-4 text-red-300" />
                  </div>
                  <p>Confirm credentials consistently across candidates</p>
                </div>

                <div className="flex items-start p-3 bg-red-900/30 rounded-lg">
                  <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1 flex-shrink-0">
                    <CheckCircle className="h-4 w-4 text-red-300" />
                  </div>
                  <p>Maintain confidential verification documentation</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-red-800/30 to-red-600/30 p-6 rounded-xl border border-red-500/20">
              <h3 className="text-2xl font-bold mb-6 text-center">
                For Candidates
              </h3>

              <div className="space-y-4">
                <div className="flex items-start p-3 bg-red-900/30 rounded-lg">
                  <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1 flex-shrink-0">
                    <CheckCircle className="h-4 w-4 text-red-300" />
                  </div>
                  <p>Provide accurate employment history</p>
                </div>

                <div className="flex items-start p-3 bg-red-900/30 rounded-lg">
                  <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1 flex-shrink-0">
                    <CheckCircle className="h-4 w-4 text-red-300" />
                  </div>
                  <p>Keep references updated and accessible</p>
                </div>

                <div className="flex items-start p-3 bg-red-900/30 rounded-lg">
                  <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1 flex-shrink-0">
                    <CheckCircle className="h-4 w-4 text-red-300" />
                  </div>
                  <p>Share correct employer contact details</p>
                </div>

                <div className="flex items-start p-3 bg-red-900/30 rounded-lg">
                  <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1 flex-shrink-0">
                    <CheckCircle className="h-4 w-4 text-red-300" />
                  </div>
                  <p>Offer additional proof if needed</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Statistics Section */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-16 initially-hidden opacity-0">
          {[
            {
              number: "90%",
              text: "of employers verify work history",
              color: "from-red-600/20 to-red-400/20",
              border: "border-red-500/30",
            },
            {
              number: "15%",
              text: "of candidates exaggerate experience",
              color: "from-pink-600/20 to-pink-400/20",
              border: "border-pink-500/30",
            },
            {
              number: "5-7",
              text: "days for traditional verification",
              color: "from-orange-600/20 to-orange-400/20",
              border: "border-orange-500/30",
            },
            {
              number: "1-3",
              text: "hours for digital verification",
              color: "from-yellow-600/20 to-yellow-400/20",
              border: "border-yellow-500/30",
            },
          ].map((stat, index) => (
            <div
              key={index}
              className={`bg-gradient-to-br ${stat.color} rounded-xl p-6 shadow-xl ${stat.border} text-center`}
            >
              <div className="text-4xl font-bold text-white mb-2">
                {stat.number}
              </div>
              <p className="text-white/80">{stat.text}</p>
            </div>
          ))}
        </div>
        {/* Call to Action */}
        <div className="bg-gradient-to-r from-red-600/30 to-pink-600/30 rounded-2xl p-8 shadow-2xl border border-red-500/20 text-center initially-hidden opacity-0">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Verify Your Workforce?
          </h2>
          <p className="text-xl text-white/80 max-w-2xl mx-auto mb-8">
            Safeguard your organization with thorough employment verification to
            ensure a skilled and trustworthy team.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 flex items-center">
              <Lock className="w-5 h-5 mr-2" />
              Get Started
            </button>
            <button className="bg-transparent border-2 border-red-400 hover:bg-red-400/20 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300">
              Learn More
            </button>
          </div>
        </div>
      </section>

      <style jsx>{`
        .animate-fadeIn {
          animation: fadeIn 1s ease forwards;
        }

        .animate-fadeInDown {
          animation: fadeInDown 1s ease forwards;
        }

        .animate-fadeInUp {
          animation: fadeInUp 1s ease forwards;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default EmploymentVerification;
